import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@vnetwork/ui/feedback';
import { useOutputStorageConfig } from '@/api/media/shared-storage';
import { useVodPresetGroups } from '@/api/media/vod-presets';
import { useNotificationCallbacks } from '@/api/media/vod-notifications';
import { useVerimatrixes } from '@/api/media/shared-verimatrix';
import { useCreateWatchFolder } from '@/api/media/vod-watch-folders';
import {
  vodFileTransferConfig,
  vodVerimatrixConfig,
} from '@/api/media/configs';
// Reuse numeric-code maps from job-create — single source of truth
import {
  OUTPUT_FORMAT_MAP,
  INGESTION_TYPE_MAP,
  DISTRIBUTION_TYPE_MAP,
} from '../../jobs/job-create/use-job-create-form';

// ============================================
// Types (re-exporting compatible shapes from job-create so we can reuse sections)
// ============================================

export type { AdditionalTrack, AdditionalTrackType, OutputKey, HlsProtectionGroup } from '../../jobs/job-create/use-job-create-form';
import type { JobFormData, OutputKey } from '../../jobs/job-create/use-job-create-form';

// WatchFolder extends JobFormData with per-format distribution paths and
// a single shared distribution target + storage (not per-format targets).
export interface WatchFolderDistribution {
  target: string;
  storage: string;
  paths: Record<OutputKey, string>;
}

export interface WatchFolderFormData extends Omit<JobFormData, 'md5sum'> {
  name: string;
  strict: boolean;
  distribution: WatchFolderDistribution;
}

// ============================================
// Constants — maps re-exported from job-create (no duplication)
// ============================================

export { OUTPUT_FORMAT_MAP, INGESTION_TYPE_MAP, DISTRIBUTION_TYPE_MAP };

const PROTOCOL_KEY_MAP: Record<string, string> = {
  ftp: 'ftp',
  http: 'https',
  sftp: 'sftp',
  webdav: 'webdav',
  s3: 's3',
};

const TARGET_KEY_MAP: Record<string, string> = {
  ftp: 'ftp',
  sftp: 'sftp',
  azure: 'https',
  s3: 's3',
  webdav: 'webdav',
};

const INITIAL_FORM: WatchFolderFormData = {
  name: '',
  strict: true,
  source: { path: '', protocol: 'ftp', storage: '', additionalTracks: [] },
  presets: [],
  outputOptions: {
    hls: {
      enabled: false, twoPass: false, multiplex: false, blackFrameRemoval: false,
      protection: 'none', noneEnabled: true, aesEnabled: false, aesKeyRotation: false,
      aesResourceId: '', verimatrixEnabled: false, verimatrixKeyRotation: false,
      verimatrixResourceId: '', verimatrixId: '',
      // watch-folder-specific extensions (not in JobFormData — stored as extra keys)
    },
    dash: { enabled: false, twoPass: false, blackFrameRemoval: false, protection: 'none' },
    mss: { enabled: false, twoPass: false, blackFrameRemoval: false, protection: 'none' },
    mp4: { enabled: false, twoPass: false, fragmented: false, blackFrameRemoval: false, protection: 'none' },
  },
  globalOverrides: { transmuxOnly: false, audioOnly: false, thumbnailSeeking: false, gpuTranscoding: false },
  distribution: {
    target: 'ftp',
    storage: '',
    paths: { hls: '', dash: '', mss: '', mp4: '' },
  },
};

// ============================================
// Helpers
// ============================================

export { getStorageLabel } from '../../jobs/job-create/use-job-create-form';

/** Legacy getIngestionType — numeric string code per INGESTION_TYPE_MAP */
function getIngestionType(protocol: string): string {
  return INGESTION_TYPE_MAP[protocol.toLowerCase()] ?? '1';
}

/** Legacy getDistributionType — numeric string code per DISTRIBUTION_TYPE_MAP */
function getDistributionType(target: string): string {
  return DISTRIBUTION_TYPE_MAP[target.toLowerCase()] ?? '3';
}

// ============================================
// Protection builder (matches legacy watchFolderSubmit.ts exactly)
//
// HLS entries:
//   none      → { type: 3 }
//   verimatrix → { type: 1, key_type: rotation?2:1, resource_policy: 1, verimatrix_id }
//   aes        → { type: 2, key_type: rotation?2:1, resource_policy: 1 }
//   fairplay   → { type: 4 }
//
// DASH entries: map protectionTypes[] → { type }  (legacy uses numeric types 3/5/6)
// mss/mp4:    → [{ type: 3 }]
// ============================================

type ProtectionEntry = Record<string, unknown>;

function buildProtectionConfigs(
  format: OutputKey,
  cfg: Record<string, unknown>,
): ProtectionEntry[] {
  if (format === 'hls') {
    const configs: ProtectionEntry[] = [];
    // none → { type: 3 } (no key_type — legacy watchFolderSubmit:51)
    if (cfg.noneEnabled) configs.push({ type: 3 });
    // verimatrix → { type:1, key_type: rot?2:1, resource_policy:1, verimatrix_id }
    if (cfg.verimatrixEnabled) {
      configs.push({
        type: 1,
        key_type: cfg.verimatrixKeyRotation ? 2 : 1,
        resource_policy: 1,
        verimatrix_id: String(cfg.verimatrixId ?? ''),
      });
    }
    // aes → { type:2, key_type: rot?2:1, resource_policy:1 }
    if (cfg.aesEnabled) {
      configs.push({
        type: 2,
        key_type: cfg.aesKeyRotation ? 2 : 1,
        resource_policy: 1,
      });
    }
    // fairplay → { type: 4 } (watch-folder-specific, not in job-create)
    if (cfg.fairplayEnabled) configs.push({ type: 4 });
    return configs.length > 0 ? configs : [{ type: 3 }];
  }

  if (format === 'dash') {
    // DASH protectionTypes multi-select: legacy sends each as { type }
    const types = Array.isArray(cfg.protectionTypes) && cfg.protectionTypes.length
      ? (cfg.protectionTypes as number[])
      : [3];
    return types.map((t) => ({ type: t }));
  }

  // mss + mp4 → none only (no key_type — legacy watchFolderSubmit:78)
  return [{ type: 3 }];
}

// ============================================
// Payload builder (matches legacy watchFolderSubmit.ts)
// ============================================

export function buildWatchFolderPayload(
  form: WatchFolderFormData,
  selectedFormats: OutputKey[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    strict: form.strict,
    ingestion_config: {
      ingestion_type: getIngestionType(form.source.protocol),
      ingestion_id: form.source.storage,
      file_path: form.source.path,
    },
    use_gpu: form.globalOverrides.gpuTranscoding,
    thumbnail_template: { id: '22' },
    transcoding_configs: selectedFormats.map((fmt) => {
      const cfg = form.outputOptions[fmt] as Record<string, unknown>;
      return {
        format: OUTPUT_FORMAT_MAP[fmt],
        mix_audio_video: fmt === 'hls' ? Boolean(cfg.multiplex) : false,
        pass: cfg.twoPass ? 2 : 1,
        has_thumbnail: Boolean(form.globalOverrides.thumbnailSeeking),
        fragment_mp4: fmt === 'mp4' ? Boolean(cfg.fragmented) : false,
        black_frame_removal: false,
        distribution_configs: [{
          distribution_type: getDistributionType(form.distribution.target),
          distribution_id: form.distribution.storage,
          path: String(form.distribution.paths[fmt] ?? '').trim(),
          encrypt_type: '3',
        }],
        protection_configs: buildProtectionConfigs(fmt, cfg),
      };
    }),
    preset_configs: form.presets.map((id) => ({ preset_id: id })),
    transmux_only: form.globalOverrides.transmuxOnly,
    audio_only: form.globalOverrides.audioOnly,
  };

  // callback only when truthy (not 0, not undefined) — legacy watchFolderSubmit:130
  if (form.callback_id) {
    payload.callback_config = { callback_id: String(form.callback_id) };
  }

  return payload;
}

// ============================================
// Validation
// ============================================

function validateForm(
  form: WatchFolderFormData,
  selectedFormats: OutputKey[],
  currentUploadStorages: Record<string, unknown>[],
): string | null {
  if (!form.name.trim()) return 'Watch folder name is required.';
  if (!form.source.protocol) return 'Source protocol is required.';
  if (!form.source.storage) return 'Source storage is required.';
  if (!form.source.path.trim()) return 'Watch path is required.';
  if (form.presets.length === 0) return 'At least one preset is required.';
  if (selectedFormats.length === 0) return 'At least one output format must be selected.';
  if (!form.distribution.target) return 'Distribution target is required.';
  if (!form.distribution.storage) return 'Distribution storage is required.';

  const storageExists = currentUploadStorages.some(
    (s) => String(s?.id ?? s?.name ?? '') === form.distribution.storage,
  );
  if (!storageExists) return 'Selected distribution storage is no longer available.';

  for (const fmt of selectedFormats) {
    if (!form.distribution.paths[fmt]?.trim()) {
      const label: Record<OutputKey, string> = { hls: 'HLS', dash: 'MPEG-DASH', mss: 'MS-SSTR', mp4: 'MP4' };
      return `Distribution path for ${label[fmt]} is required.`;
    }
  }

  // HLS + Verimatrix requires verimatrixId — legacy watchFolderSubmit:30-37
  if (selectedFormats.includes('hls')) {
    const hlsCfg = form.outputOptions.hls as Record<string, unknown>;
    if (hlsCfg.verimatrixEnabled && !String(hlsCfg.verimatrixId ?? '').trim()) {
      return 'Please select Verimatrix DRM for HLS Verimatrix protection.';
    }
  }

  return null;
}

// ============================================
// Hook
// ============================================

export function useWatchFolderCreateForm(serviceId: string) {
  const navigate = useNavigate();
  const toast = useToast();

  const { data: outputs, isLoading: outputsLoading } = useOutputStorageConfig(serviceId, vodFileTransferConfig);
  const { data: presets, isLoading: presetsLoading } = useVodPresetGroups(serviceId);
  const { data: notifications = [], isLoading: notifLoading } = useNotificationCallbacks(serviceId);
  const { data: verimatrixes = [] } = useVerimatrixes(serviceId, vodVerimatrixConfig);
  const createWatchFolder = useCreateWatchFolder(serviceId);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<WatchFolderFormData>(INITIAL_FORM);

  // Derive current source storages from protocol selection
  const currentStorages = (() => {
    const key = PROTOCOL_KEY_MAP[form.source.protocol];
    if (!key || !outputs) return [];
    const list = (outputs as unknown as Record<string, unknown[]>)[key];
    return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
  })();

  // Derive current distribution storages from distribution target
  const currentUploadStorages = (() => {
    const key = TARGET_KEY_MAP[form.distribution.target];
    if (!key || !outputs) return [];
    const list = (outputs as unknown as Record<string, unknown[]>)[key];
    return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
  })();

  // Auto-select first source storage when protocol changes
  useEffect(() => {
    if (!currentStorages.length) return;
    setForm((prev) => {
      const still = currentStorages.some(
        (s) => String(s?.id ?? s?.name ?? '') === prev.source.storage,
      );
      if (still && prev.source.storage) return prev;
      return {
        ...prev,
        source: { ...prev.source, storage: String(currentStorages[0]?.id ?? currentStorages[0]?.name ?? '') },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.source.protocol, outputs]);

  // Auto-select first distribution target+storage when outputs load
  useEffect(() => {
    if (!outputs) return;
    const targetOrder = ['ftp', 'sftp', 's3', 'azure', 'webdav'];
    setForm((prev) => {
      if (prev.distribution.target && prev.distribution.storage) return prev;
      for (const t of targetOrder) {
        const key = TARGET_KEY_MAP[t];
        const list = key ? (outputs as unknown as Record<string, unknown[]>)[key] : [];
        const storages = Array.isArray(list) ? list : [];
        if (storages.length > 0) {
          return {
            ...prev,
            distribution: {
              ...prev.distribution,
              target: t,
              storage: String((storages[0] as Record<string, unknown>)?.id ?? (storages[0] as Record<string, unknown>)?.name ?? ''),
            },
          };
        }
      }
      return prev;
    });
  }, [outputs]);

  // Auto-select first distribution storage when target changes
  useEffect(() => {
    if (!currentUploadStorages.length || !form.distribution.target) return;
    setForm((prev) => {
      const still = currentUploadStorages.some(
        (s) => String(s?.id ?? s?.name ?? '') === prev.distribution.storage,
      );
      if (still && prev.distribution.storage) return prev;
      return {
        ...prev,
        distribution: {
          ...prev.distribution,
          storage: String(currentUploadStorages[0]?.id ?? currentUploadStorages[0]?.name ?? ''),
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.distribution.target, outputs]);

  // ---- Derived ----

  const selectedStorage = currentStorages.find(
    (s) => String(s?.id ?? s?.name ?? '') === form.source.storage,
  );

  // isOutputSelected: driven exclusively by the `enabled` flag — mirrors legacy Boolean(cfg.enabled)
  const isOutputSelected = (key: OutputKey): boolean =>
    Boolean((form.outputOptions[key] as Record<string, unknown>).enabled);

  const selectedFormats = (Object.keys(form.outputOptions) as OutputKey[]).filter(isOutputSelected);

  // ---- Mutators (compatible with job-create section components) ----

  const updateSource = (field: string, value: string) =>
    setForm((p) => ({ ...p, source: { ...p.source, [field]: value } }));

  const updateMd5sum = (_v: string) => { /* Watch folders don't use md5sum */ };

  const addAdditionalTrack = (track: import('../../jobs/job-create/use-job-create-form').AdditionalTrack) =>
    setForm((p) => ({ ...p, source: { ...p.source, additionalTracks: [...p.source.additionalTracks, track] } }));

  const removeAdditionalTrack = (index: number) =>
    setForm((p) => ({
      ...p,
      source: { ...p.source, additionalTracks: p.source.additionalTracks.filter((_, i) => i !== index) },
    }));

  const togglePreset = (value: string) =>
    setForm((p) => ({
      ...p,
      presets: p.presets.includes(value) ? p.presets.filter((k) => k !== value) : [...p.presets, value],
    }));

  /** Toggle card: only flips `enabled`, does not touch other fields */
  const toggleOutputCard = (key: OutputKey) => {
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        [key]: { ...(p.outputOptions[key] as Record<string, unknown>), enabled: !isOutputSelected(key) },
      },
    }));
  };

  const toggleCardOption = (key: OutputKey, field: string) =>
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        [key]: {
          ...(p.outputOptions[key] as Record<string, unknown>),
          enabled: true,
          [field]: !(p.outputOptions[key] as Record<string, unknown>)[field],
        },
      },
    }));

  const updateOutputOption = (key: OutputKey, field: string, value: unknown) =>
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        [key]: { ...(p.outputOptions[key] as Record<string, unknown>), enabled: true, [field]: value },
      },
    }));

  /** Toggle HLS protection flags (noneEnabled / aesEnabled / verimatrixEnabled / fairplayEnabled) */
  const toggleHlsProtection = (
    field: 'noneEnabled' | 'aesEnabled' | 'verimatrixEnabled' | 'fairplayEnabled',
    checked: boolean,
  ) =>
    setForm((p) => ({
      ...p,
      outputOptions: {
        ...p.outputOptions,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hls: { ...(p.outputOptions.hls as any), enabled: true, [field]: checked } as any,
      },
    } as WatchFolderFormData));

  /** Toggle DASH protectionType value (3 = none, 5 = Widevine, 6 = PlayReady) */
  const toggleDashProtection = (type: 3 | 5 | 6, checked: boolean) => {
    setForm((p) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dashCfg = p.outputOptions.dash as any;
      const current: number[] = Array.isArray(dashCfg.protectionTypes)
        ? dashCfg.protectionTypes
        : [3];
      const next = checked
        ? Array.from(new Set([...current, type]))
        : current.filter((t) => t !== type);
      return {
        ...p,
        outputOptions: {
          ...p.outputOptions,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dash: { ...dashCfg, enabled: true, protectionTypes: next } as any,
        },
      } as WatchFolderFormData;
    });
  };

  const updateGlobalOverride = (field: string, value: boolean) =>
    setForm((p) => ({ ...p, globalOverrides: { ...p.globalOverrides, [field]: value } }));

  // Watch-folder specific distribution mutators
  const updateDistributionTarget = (target: string) =>
    setForm((p) => ({ ...p, distribution: { ...p.distribution, target, storage: '' } }));

  const updateDistributionStorage = (storage: string) =>
    setForm((p) => ({ ...p, distribution: { ...p.distribution, storage } }));

  const updateDistributionPath = (fmt: OutputKey, path: string) =>
    setForm((p) => ({
      ...p,
      distribution: {
        ...p.distribution,
        paths: { ...p.distribution.paths, [fmt]: path },
      },
    }));

  const updateNotification = (value?: number) =>
    setForm((p) => ({ ...p, callback_id: value }));

  const updateGeneral = (field: string, value: unknown) =>
    setForm((p) => ({ ...p, [field]: value }));

  const getStoragesByTarget = (target: string): Record<string, unknown>[] => {
    const key = TARGET_KEY_MAP[target.toLowerCase()];
    if (!key || !outputs) return [];
    const list = (outputs as unknown as Record<string, unknown[]>)[key];
    return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
  };

  // ---- Submit ----

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateForm(form, selectedFormats, currentUploadStorages);
    if (error) {
      setFormError(error);
      return;
    }

    setFormError(null);
    setIsSubmitting(true);
    try {
      const payload = buildWatchFolderPayload(form, selectedFormats);
      await createWatchFolder.mutateAsync(payload as Parameters<typeof createWatchFolder.mutateAsync>[0]);
      toast.success('Watch folder created successfully');
      navigate(`/services/vod/${serviceId}/watch-folders`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create watch folder';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    currentStorages,
    currentUploadStorages,
    selectedStorage,
    selectedFormats,
    presets: presets ?? [],
    notifications,
    verimatrixes,
    isPageLoading: outputsLoading || presetsLoading || notifLoading,
    isSubmitting,
    formError,
    navigate,
    updateSource,
    updateMd5sum,
    addAdditionalTrack,
    removeAdditionalTrack,
    togglePreset,
    isOutputSelected,
    toggleOutputCard,
    toggleCardOption,
    updateOutputOption,
    toggleHlsProtection,
    toggleDashProtection,
    updateGlobalOverride,
    updateDistributionTarget,
    updateDistributionStorage,
    updateDistributionPath,
    updateNotification,
    updateGeneral,
    getStoragesByTarget,
    handleSubmit,
  };
}
